import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, QrCode, Share2 } from 'lucide-react';
import { Button, Card, CardBody } from '@nexply/ui';
import { api } from '../lib/api.js';
import { QRModal } from './QRModal.js';

export function InviteLinkCard() {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  // Der Link wird serverseitig beim ersten Abruf erzeugt — deshalb reicht ein GET.
  const q = useQuery({ queryKey: ['account', 'invite-link'], queryFn: () => api.account.inviteLink() });

  const url = q.data ? `${window.location.origin}/join/${q.data.slug}` : '';

  const copy = useMutation({
    mutationFn: async () => {
      await navigator.clipboard.writeText(url);
    },
    onSuccess: () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
  });

  async function share() {
    if (navigator.share) {
      await navigator.share({ title: 'nexply', text: 'Trag dich in mein Netzwerk ein:', url }).catch(() => {});
      return;
    }
    copy.mutate();
  }

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-text">Dein Einladungslink</div>
            <p className="mt-0.5 text-xs text-text-muted">
              Wer den Link öffnet, trägt sich ohne Anmeldung selbst in dein Netzwerk ein.
            </p>
          </div>
          {q.data && (
            <div className="shrink-0 rounded-lg border border-line bg-raise px-3 py-1.5 text-center">
              <div className="text-lg font-semibold tabular-nums text-text">{q.data.signups}</div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">
                Eintragungen
              </div>
            </div>
          )}
        </div>

        {q.isLoading && <div className="h-11 rounded-xl bg-raise animate-pulse" />}
        {q.isError && (
          <div className="text-xs text-danger">Der Einladungslink konnte nicht geladen werden.</div>
        )}

        {q.data && (
          <>
            <div className="rounded-xl border border-line bg-surface-2 px-3 py-2.5 text-sm text-text-dim break-all">
              {url}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => copy.mutate()}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Kopiert' : 'Link kopieren'}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setQrOpen(true)}>
                <QrCode className="h-4 w-4" />
                QR-Code
              </Button>
              <Button size="sm" variant="ghost" onClick={share}>
                <Share2 className="h-4 w-4" />
                Teilen
              </Button>
            </div>
            {copy.isError && (
              <div className="text-xs text-danger">
                Kopieren hat nicht geklappt — markiere den Link oben einfach von Hand.
              </div>
            )}
            <QRModal
              open={qrOpen}
              onClose={() => setQrOpen(false)}
              value={url}
              title="QR-Code · Einladungslink"
              downloadName="nexply-einladung-qr.png"
            />
          </>
        )}
      </CardBody>
    </Card>
  );
}
